import Grid from "@mui/material/Grid2";
import { useEvents } from "api/events";
import { EmptyState } from "components/EmptyState/EmptyState";
import { FormSection } from "components/FormControls/FormSection";
import { HelpPanel } from "components/HelpPanel/HelpPanel";
import { ReactComponent as RuleIcon } from "components/Icons/ruleIcon.svg";
import {
  filterOldTierConditions,
  generateSchemaConditions,
} from "components/Layout/Campaign/Common/CampaignRules/rulesHelpers";
import { FieldArray } from "formik";
import { useMessage } from "hooks/intl";
import { OLButton } from "OLButton";
import { memo, useState } from "react";
import {
  CampaignTriggers,
  CampaignTriggersUnion,
  DirectCampaignRule,
  SelectedSchemaCondition,
  TransactionItem,
} from "types/campaigns";
import { TriggerBox } from "../Triggers/TriggerBox";
import { Menu } from "./Menu/Menu";
import { Rule, RuleError } from "./Rule/Rule";
import { useStyles } from "./styles";
import { TransactionItemFilters } from "./TransactionItemFilters/TransactionItemFilters";
import { getTriggerConditions } from "./utils";

interface Props {
  rules: DirectCampaignRule[];
  trigger: CampaignTriggersUnion;
  event?: string;
  transactionItemsFilters?: TransactionItem[];
  errors: any;
  isEditMode: boolean;
}

export const Rules = memo(
  ({
    rules,
    trigger,
    event,
    transactionItemsFilters,
    errors,
    isEditMode,
  }: Props) => {
    const [isFilterSidebarOpen, setIsFilterSidebarOpen] = useState(false);
    const [activeRule, setActiveRule] = useState(0);
    const { message } = useMessage();
    const classes = useStyles();
    const triggerConditions = getTriggerConditions(trigger, event);
    const { useCustomEventSchemaDetails } = useEvents();

    const { data: selectedSchemaDetails } = useCustomEventSchemaDetails(
      trigger !== CampaignTriggers.INTERNAL_EVENT ? event : undefined,
    );

    const selectedSchemaConditions: SelectedSchemaCondition[] =
      generateSchemaConditions(selectedSchemaDetails?.schema?.fields);
    const conditions = [
      ...triggerConditions,
      ...(trigger === CampaignTriggers.CUSTOM_EVENT ||
      trigger === CampaignTriggers.CUSTOM_EVENT_UNIQUE_CODE
        ? selectedSchemaConditions
        : []),
    ];

    const filteredConditions = filterOldTierConditions({
      conditions,
      isEditMode,
      rules,
    });

    const isGeneralError =
      errors?.rules &&
      Array.isArray(errors.rules) &&
      typeof errors?.rules[0] === "string";

    const showTransactionFilters = !!(
      trigger === CampaignTriggers.TRANSACTION ||
      trigger === CampaignTriggers.RETURN_TRANSACTION
    );

    return (
      <FieldArray
        name="rules"
        render={({ push, remove, form }) => {
          const { errors, setFieldError, setFieldValue, values } = form;
          const maxRulesCount = 6;
          const isMaxRulesCount = rules.length >= maxRulesCount;
          const areRulesAdded = rules && rules.length > 0;
          const ruleData = rules[activeRule];
          const currentError = errors.rules && (errors.rules as [])[activeRule];
          const transactionFiltersError = !!(
            errors && errors.transactionItemsFilters
          );

          return (
            <Grid container spacing={12}>
              <TransactionItemFilters
                errors={errors}
                isOpen={isFilterSidebarOpen}
                close={() => setIsFilterSidebarOpen(false)}
                setFieldValue={setFieldValue}
                values={values}
              />
              <Grid size={{ xs: 8 }}>
                <FormSection
                  title={
                    <div className={classes.headerWrapper}>
                      <div className={classes.sectionTitleWrapper}>
                        {message({ id: "rules" })}
                        <TriggerBox />
                      </div>

                      {showTransactionFilters && (
                        <OLButton
                          variant="outlined"
                          className={
                            transactionFiltersError ? classes.error : ""
                          }
                          type="button"
                          onClick={() => {
                            setFieldValue(
                              "drafts.transactionItemsFilters",
                              values.transactionItemsFilters,
                              false,
                            );

                            setIsFilterSidebarOpen(true);
                          }}
                          data-test-id="transaction-item-filters"
                        >
                          {`${message({ id: "transactionFilters" })} (${transactionItemsFilters?.length || 0})`}
                        </OLButton>
                      )}
                    </div>
                  }
                  noPadding
                  sectionError={errors.rules as string}
                >
                  {areRulesAdded ? (
                    <>
                      <section className={classes.section}>
                        <div className={classes.menuContainer}>
                          <Menu
                            activeRule={activeRule}
                            setActiveRule={setActiveRule}
                            rulesCount={rules.length}
                            push={push}
                            remove={remove}
                            errors={
                              errors.rules
                                ? (errors.rules as { [key: number]: any })
                                : {}
                            }
                            isEditMode={isEditMode}
                            rules={rules}
                          />
                        </div>
                      </section>
                      <section className={classes.rulesSection}>
                        {ruleData && (
                          <Rule
                            index={activeRule}
                            removeRule={remove}
                            rule={ruleData}
                            conditions={filteredConditions}
                            error={currentError as RuleError}
                            selectedSchemaConditions={selectedSchemaConditions}
                            trigger={trigger}
                            transactionItemsFilters={transactionItemsFilters}
                          />
                        )}
                      </section>
                    </>
                  ) : (
                    <EmptyState
                      icon={<RuleIcon />}
                      title={message({
                        id: "addRulesAndConfigureConditionsAndEffects",
                      })}
                      description={message({ id: "addRulesDescription" })}
                      button={
                        <OLButton
                          color="primary"
                          data-test-id={"campaign-rules-add"}
                          onClick={() => {
                            if (isGeneralError) {
                              setFieldError("rules", undefined);
                            }

                            push({});
                          }}
                          variant="contained"
                          size="medium"
                          disabled={isMaxRulesCount}
                        >
                          {message({ id: "adddNewRule" })}
                        </OLButton>
                      }
                    />
                  )}
                </FormSection>
              </Grid>
              <Grid size={{ xs: 4 }} mt={27}>
                <HelpPanel
                  title="rules&effects"
                  description="rulesAndEffectDescription"
                />
                {showTransactionFilters && (
                  <HelpPanel
                    title="transactionFilters"
                    description="transactionFiltersDesc"
                  />
                )}
              </Grid>
            </Grid>
          );
        }}
      />
    );
  },
);
